import { isSessionRejection, SessionEndedError } from './sessionErrors'
import type { UserManager } from 'oidc-client-ts'

// Credentials stay in sessionStorage. Only a random revision/logout marker is persistent.
// A new tab obtains the active session directly from another open same-origin tab.
export class TabUserStore {
  private readonly channel: BroadcastChannel | undefined
  private readonly revisionKey: string
  private readonly waiting = new Map<string, (value: string | null) => void>()
  private readonly listeners = new Set<() => void>()
  constructor(private readonly key: string) {
    this.revisionKey = `${key}:revision`
    if (!localStorage.getItem(this.revisionKey) && sessionStorage.getItem(key)) {
      const revision = `active:${crypto.randomUUID()}`
      localStorage.setItem(this.revisionKey, revision)
      sessionStorage.setItem(this.revisionKey, revision)
    }
    this.channel = typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(key)
    if (this.channel)
      this.channel.onmessage = ({ data }) => {
        const current = localStorage.getItem(this.revisionKey)
        if (!data || data.revision !== current) return
        if (data.type === 'request') {
          const value = this.current()
          if (value)
            this.channel?.postMessage({
              type: 'value',
              request: data.request,
              revision: current,
              value,
            })
        } else if (
          data.type === 'value' &&
          typeof data.value === 'string' &&
          current?.startsWith('active:')
        ) {
          sessionStorage.setItem(this.key, data.value)
          sessionStorage.setItem(this.revisionKey, current)
          if (data.request) this.waiting.get(data.request)?.(data.value)
          this.listeners.forEach((listener) => listener())
        }
      }
    window.addEventListener('storage', this.changed)
    if (import.meta.hot)
      import.meta.hot.dispose(() => {
        this.channel?.close()
        window.removeEventListener('storage', this.changed)
      })
  }
  private changed = (event: StorageEvent) => {
    if (
      event.storageArea !== localStorage ||
      (event.key !== this.revisionKey && event.key !== null)
    )
      return
    if (!event.newValue || event.newValue.startsWith('ended:')) {
      sessionStorage.removeItem(this.key)
      sessionStorage.removeItem(this.revisionKey)
      this.waiting.forEach((resolve) => resolve(null))
      this.listeners.forEach((listener) => listener())
    }
  }
  private current(): string | null {
    const revision = localStorage.getItem(this.revisionKey)
    return revision?.startsWith('active:') && revision === sessionStorage.getItem(this.revisionKey)
      ? sessionStorage.getItem(this.key)
      : null
  }
  async get(key: string): Promise<string | null> {
    if (`oidc.${key}` !== this.key) return null
    const value = this.current()
    if (value) return value
    const revision = localStorage.getItem(this.revisionKey)
    if (!this.channel || !revision?.startsWith('active:')) return null
    return new Promise((resolve) => {
      const request = crypto.randomUUID()
      const timer = window.setTimeout(() => finish(null), 1200)
      const finish = (value: string | null) => {
        clearTimeout(timer)
        this.waiting.delete(request)
        resolve(localStorage.getItem(this.revisionKey) === revision ? value : null)
      }
      this.waiting.set(request, finish)
      this.channel!.postMessage({ type: 'request', request, revision })
    })
  }
  async set(key: string, value: string): Promise<void> {
    if (`oidc.${key}` !== this.key) throw new Error('Unexpected user storage key')
    const revision = `active:${crypto.randomUUID()}`
    sessionStorage.setItem(this.key, value)
    sessionStorage.setItem(this.revisionKey, revision)
    localStorage.setItem(this.revisionKey, revision)
    this.channel?.postMessage({ type: 'value', revision, value })
  }
  async remove(key: string): Promise<string | null> {
    if (`oidc.${key}` !== this.key) return null
    const value = sessionStorage.getItem(this.key)
    sessionStorage.removeItem(this.key)
    sessionStorage.removeItem(this.revisionKey)
    localStorage.setItem(this.revisionKey, `ended:${crypto.randomUUID()}`)
    return value
  }
  async getAllKeys(): Promise<string[]> {
    return this.current() ? [this.key.slice(5)] : []
  }
  onChanged(listener: () => void) {
    this.listeners.add(listener)
  }
}

export function sharedSession(manager: UserManager, store: TabUserStore, key: string) {
  store.onChanged(() => {
    void manager
      .getUser()
      .then((user) => (user ? manager.events.load(user) : manager.events.unload()))
      .catch(() => { /* Storage/transport failure does not prove sign-out. */ })
  })
  // Serialise renewal/logout across tabs; getUser then retrieves the current revision.
  const exclusive = async <T>(action: () => Promise<T>): Promise<T> =>
    navigator.locks ? await navigator.locks.request(`session:${key}`, action) : action()
  let pending: Promise<string> | undefined
  const accessToken = (): Promise<string> => {
    pending ??= exclusive(async () => {
      let user = await manager.getUser()
      if (!user) throw new SessionEndedError()
      if ((user.expires_in ?? 0) < 30) {
        if (!user.refresh_token) {
          await manager.removeUser()
          throw new SessionEndedError()
        }
        try {
          user = await manager.signinSilent()
        } catch (error) {
          if (isSessionRejection(error)) {
            await manager.removeUser()
            throw new SessionEndedError()
          }
          throw error
        }
      }
      if (!user || user.expired) throw new SessionEndedError()
      return user.access_token
    }).finally(() => {
      pending = undefined
    })
    return pending
  }
  return { accessToken, exclusive }
}
